import React, { useState, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Upload, X, AlertCircle, Clock, GripVertical, Replace } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import type { InquiryAttachmentDto } from '@paladin-monorepo/common';

export type FileUploadItem = InquiryAttachmentDto & { _isQueued?: boolean };

interface FileUploadProps {
  onFileUpload: (files: File[]) => void;
  onFileRemove: (fileId: string) => void;
  onFileUpdate: (fileId: string, updates: { description?: string; ndaProtected?: boolean }) => void;
  files: FileUploadItem[];
  maxSize?: number;
  acceptedTypes?: string[];
  className?: string;
  ndaRequired?: boolean;
  sortable?: boolean;
  onReorder?: (orderedIds: string[]) => void;
  onFileReplace?: (fileId: string, file: File) => void;
}

function SortableFileRow({
  file,
  uploadProgress,
  ndaRequired,
  onFileRemove,
  onFileUpdate,
  getFileIcon,
  formatFileSize,
  t,
  onFileReplace,
}: {
  file: FileUploadItem;
  uploadProgress: Record<string, number>;
  ndaRequired: boolean;
  onFileRemove: (fileId: string) => void;
  onFileUpdate: (fileId: string, updates: { description?: string; ndaProtected?: boolean }) => void;
  getFileIcon: (mimeType: string) => string;
  formatFileSize: (bytes: number) => string;
  t: (key: string, options?: Record<string, unknown>) => string;
  onFileReplace?: (fileId: string, file: File) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: file.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="p-3 bg-muted rounded-lg space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 min-w-0">
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground shrink-0"
            aria-label={t('fileUpload.reorder')}
            {...attributes}
            {...listeners}
          >
            <GripVertical size={16} />
          </button>
          <span className="text-lg shrink-0">{getFileIcon(file.mimeType)}</span>
          <div className="min-w-0">
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium text-foreground truncate">{file.originalName}</p>
              {file._isQueued && (
                <Badge variant="secondary" className="text-xs shrink-0">
                  <Clock size={10} className="mr-1" />
                  Queued
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2 shrink-0">
          {uploadProgress[file.id] !== undefined && (
            <div className="w-20">
              <Progress value={uploadProgress[file.id]} className="h-1" />
            </div>
          )}
          {onFileReplace && !file._isQueued && (
            <ReplaceFileButton
              fileId={file.id}
              label={t('fileUpload.replace')}
              onFileReplace={onFileReplace}
            />
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onFileRemove(file.id)}
            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950"
          >
            <X size={14} />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <div>
          <label className="text-xs font-medium text-muted-foreground">
            {t('fileUpload.description.label')}
          </label>
          <Input
            placeholder={t('fileUpload.description.placeholder')}
            value={file.description || ''}
            onChange={(e) => onFileUpdate(file.id, { description: e.target.value })}
            className="mt-1 text-sm"
          />
        </div>

        {ndaRequired && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`nda-${file.id}`}
              checked={file.ndaProtected || false}
              onCheckedChange={(checked) => onFileUpdate(file.id, { ndaProtected: !!checked })}
            />
            <label htmlFor={`nda-${file.id}`} className="text-xs font-medium text-muted-foreground">
              {t('fileUpload.ndaProtected.label')}
            </label>
          </div>
        )}
      </div>
    </div>
  );
}

function ReplaceFileButton({
  fileId,
  label,
  onFileReplace,
}: {
  fileId: string;
  label: string;
  onFileReplace: (fileId: string, file: File) => void;
}) {
  const inputId = `replace-file-${fileId}`;
  return (
    <>
      <input
        id={inputId}
        type="file"
        className="sr-only"
        onChange={(e) => {
          const next = e.target.files?.[0];
          if (next) onFileReplace(fileId, next);
          e.target.value = '';
        }}
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        aria-label={label}
        onClick={() => document.getElementById(inputId)?.click()}
      >
        <Replace size={14} />
      </Button>
    </>
  );
}

export function FileUpload({
  onFileUpload,
  onFileRemove,
  onFileUpdate,
  files = [],
  maxSize = 25 * 1024 * 1024,
  acceptedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-powerpoint'],
  className,
  ndaRequired = false,
  sortable = false,
  onReorder,
  onFileReplace,
}: FileUploadProps) {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress] = useState<Record<string, number>>({});

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const fileIds = useMemo(() => files.map((file) => file.id), [files]);

  const validateFile = (file: File): string | null => {
    if (file.size > maxSize) {
      return t('fileUpload.fileSizeExceeds', { maxSize: Math.round(maxSize / (1024 * 1024)) });
    }

    if (acceptedTypes.length > 0 && !acceptedTypes.includes(file.type)) {
      return t('fileUpload.fileTypeNotAllowed');
    }

    return null;
  };

  const handleFiles = useCallback((fileList: FileList | File[]) => {
    const filesArray = Array.from(fileList);
    const validFiles: File[] = [];
    const errors: string[] = [];

    filesArray.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
    }

    if (validFiles.length > 0) {
      onFileUpload(validFiles);
    }
  }, [maxSize, acceptedTypes, onFileUpload, t]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  }, [handleFiles]);

  const handleSortEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = fileIds.indexOf(String(active.id));
      const newIndex = fileIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;

      onReorder?.(arrayMove(fileIds, oldIndex, newIndex));
    },
    [fileIds, onReorder],
  );

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return `0 ${t('fileUpload.fileSizeUnits.bytes')}`;
    const k = 1024;
    const sizes = ['B', 'kB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return '📊';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📈';
    return '📁';
  };

  const renderFileRow = (file: FileUploadItem, index: number) => {
    if (sortable) {
      return (
        <SortableFileRow
          key={file.id || index}
          file={file}
          uploadProgress={uploadProgress}
          ndaRequired={ndaRequired}
          onFileRemove={onFileRemove}
          onFileUpdate={onFileUpdate}
          getFileIcon={getFileIcon}
          formatFileSize={formatFileSize}
          t={t}
          onFileReplace={onFileReplace}
        />
      );
    }

    return (
      <div key={file.id || index} className="p-3 bg-muted rounded-lg space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-lg">{getFileIcon(file.mimeType)}</span>
            <div>
              <div className="flex items-center space-x-2">
                <p className="text-sm font-medium text-foreground">{file.originalName}</p>
                {file._isQueued && (
                  <Badge variant="secondary" className="text-xs">
                    <Clock size={10} className="mr-1" />
                    Queued
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {uploadProgress[file.id] !== undefined && (
              <div className="w-20">
                <Progress value={uploadProgress[file.id]} className="h-1" />
              </div>
            )}
            {onFileReplace && !file._isQueued && (
              <ReplaceFileButton
                fileId={file.id}
                label={t('fileUpload.replace')}
                onFileReplace={onFileReplace}
              />
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onFileRemove(file.id)}
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950"
            >
              <X size={14} />
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              {t('fileUpload.description.label')}
            </label>
            <Input
              placeholder={t('fileUpload.description.placeholder')}
              value={file.description || ''}
              onChange={(e) => onFileUpdate(file.id, { description: e.target.value })}
              className="mt-1 text-sm"
            />
          </div>

          {ndaRequired && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`nda-${file.id}`}
                checked={file.ndaProtected || false}
                onCheckedChange={(checked) => onFileUpdate(file.id, { ndaProtected: !!checked })}
              />
              <label htmlFor={`nda-${file.id}`} className="text-xs font-medium text-muted-foreground">
                {t('fileUpload.ndaProtected.label')}
              </label>
            </div>
          )}
        </div>
      </div>
    );
  };

  const fileList = (
    <div className="mt-4 space-y-2">
      <h4 className="text-sm font-medium text-foreground">{t('fileUpload.uploadedFiles')}</h4>
      {sortable ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSortEnd}>
          <SortableContext items={fileIds} strategy={verticalListSortingStrategy}>
            {files.map((file, index) => renderFileRow(file, index))}
          </SortableContext>
        </DndContext>
      ) : (
        files.map((file, index) => renderFileRow(file, index))
      )}
    </div>
  );

  return (
    <div className={className}>
      <Card
        className={`border-2 border-dashed transition-colors ${
          isDragOver
            ? 'border-blue-400 bg-blue-50 dark:border-blue-500 dark:bg-blue-950'
            : 'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <CardContent className="flex flex-col items-center justify-center py-8">
          <Upload className="h-12 w-12 text-muted-foreground mb-4" />
          <div className="text-center">
            <p className="text-sm font-medium text-foreground mb-1">
              {t('fileUpload.dropFilesHere')}{' '}
              <label className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer">
                {t('fileUpload.browse')}
                <input
                  type="file"
                  multiple
                  accept={acceptedTypes.join(',')}
                  onChange={handleFileInput}
                  className="hidden"
                />
              </label>
            </p>
            <p className="text-xs text-muted-foreground">
              {t('fileUpload.fileTypes', { maxSize: Math.round(maxSize / (1024 * 1024)) })}
            </p>
          </div>
        </CardContent>
      </Card>

      {files.length > 0 && fileList}

      {files.length === 0 && (
        <div className="mt-4 flex items-center space-x-2 text-sm text-muted-foreground">
          <AlertCircle size={16} />
          <span>{t('fileUpload.noFilesUploaded')}</span>
        </div>
      )}
    </div>
  );
}
